/**
 * demo_loading_controller.js — full-screen provisioning progress overlay (Stimulus).
 *
 * Originally built for demo-account creation; now the shared (SSOT) loader for any
 * slow "create" POST that 302s into a different asset shell — demo provisioning AND
 * new-business creation. Attached to the overlay included once in public_base.html
 * (templates/components/demo_loading_overlay.html).
 *
 * Trigger (document-level submit, capture phase, so no per-form wiring for the
 * dozen try-demo CTAs): a form matches when EITHER its action path equals
 * data-demo-url (the try-demo endpoint, default copy) OR it carries the
 * data-loading-overlay attribute (opt-in, with optional data-loading-{eyebrow,
 * title,messages} copy overrides). Forms that do neither submit normally.
 *
 * Finish behaviour: the matched submit is taken over with fetch so completion is a
 * real signal, not a guess. The bar eases toward ~90% while the request is in
 * flight (CSS transition on .is-active); when the response resolves it snaps to
 * 100% (.is-complete) and only THEN navigates to the redirect target. This is the
 * fix for the old "bar stops halfway then the page appears" — the navigation no
 * longer races a fixed-duration animation.
 *
 * On a network error the native form submit runs as a fallback so nothing is lost.
 * A submitting flag blocks double-submits (impatient double-clicks would otherwise
 * provision twice). pageshow (bfcache restore via the back button) resets the
 * overlay so the page is usable again.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        static targets = ['eyebrow', 'title', 'message'];

        connect() {
            this._timers = [];
            this._submitting = false;
            this._defaults = this._captureDefaults();
            this._onSubmit = this._handleSubmit.bind(this);
            this._onPageshow = this._reset.bind(this);
            document.addEventListener('submit', this._onSubmit, true);
            window.addEventListener('pageshow', this._onPageshow);
        }

        disconnect() {
            document.removeEventListener('submit', this._onSubmit, true);
            window.removeEventListener('pageshow', this._onPageshow);
            this._clearTimers();
        }

        _handleSubmit(event) {
            var form = event.target;
            // getAttribute, not form.action: a control named "action" (the hidden
            // input that selects the auth.account branch) shadows the .action property.
            var action = form && form.getAttribute('action');
            if (!action || event.defaultPrevented) return;
            var formPath;
            try {
                formPath = new URL(action, window.location.origin).pathname;
            } catch (_e) {
                return;
            }
            var isDemo = formPath === this.element.dataset.demoUrl;
            var optedIn = form.hasAttribute('data-loading-overlay');
            if (!isDemo && !optedIn) return;

            // Take over submission so we control the finish-then-navigate sequence.
            event.preventDefault();
            if (this._submitting) return;
            this._submitting = true;

            this._applyCopy(optedIn ? form : null);
            this._show();
            this._submit(form);
        }

        _submit(form) {
            var self = this;
            var action = form.getAttribute('action');
            var method = (form.getAttribute('method') || 'POST').toUpperCase();
            window.fetch(action, {
                method: method,
                body: new FormData(form),
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            }).then(function (resp) {
                // Success paths (demo + create-business) always 302 into the shell.
                // Finish the bar, then navigate to wherever the server sent us.
                self._complete(function () { window.location.href = resp.url; });
            }).catch(function () {
                // Network failure: hand back to a normal browser submit so the
                // user's input is not lost behind a stuck overlay.
                self._reset();
                form.submit();
            });
        }

        _show() {
            this.element.classList.add('is-active');
            document.body.classList.add('demo-loading-lock');
            this._rotateMessages();
        }

        _complete(done) {
            this.element.classList.add('is-complete');
            this._clearTimers();
            // Let the bar visibly run to 100% (CSS transition ~0.45s) before leaving.
            this._timers.push(window.setTimeout(done, 550));
        }

        _reset() {
            this.element.classList.remove('is-active', 'is-complete');
            document.body.classList.remove('demo-loading-lock');
            this._submitting = false;
            this._clearTimers();
            this._applyCopy(null);
        }

        // Pass the triggering form to override copy; pass null to restore demo defaults.
        _applyCopy(form) {
            var eyebrow = form ? form.getAttribute('data-loading-eyebrow') : null;
            var title = form ? form.getAttribute('data-loading-title') : null;
            var messages = form ? form.getAttribute('data-loading-messages') : null;

            if (this.hasEyebrowTarget) {
                this.eyebrowTarget.textContent = eyebrow != null ? eyebrow : this._defaults.eyebrow;
            }
            if (this.hasTitleTarget) {
                this.titleTarget.textContent = title != null ? title : this._defaults.title;
            }
            if (this.hasMessageTarget) {
                this.messageTarget.dataset.messages = messages != null ? messages : this._defaults.messages;
                var first = this._messages()[0];
                if (first) this.messageTarget.textContent = first;
            }
        }

        _captureDefaults() {
            return {
                eyebrow: this.hasEyebrowTarget ? this.eyebrowTarget.textContent : '',
                title: this.hasTitleTarget ? this.titleTarget.textContent : '',
                messages: this.hasMessageTarget ? (this.messageTarget.dataset.messages || '[]') : '[]'
            };
        }

        _rotateMessages() {
            if (!this.hasMessageTarget) return;
            var el = this.messageTarget;
            var messages = this._messages();
            var self = this;
            messages.slice(1).forEach(function (msg, i) {
                self._timers.push(window.setTimeout(function () {
                    el.textContent = msg;
                }, (i + 1) * 3000));
            });
        }

        _messages() {
            try {
                return JSON.parse(this.messageTarget.dataset.messages || '[]');
            } catch (_e) {
                return [];
            }
        }

        _clearTimers() {
            this._timers.forEach(window.clearTimeout);
            this._timers = [];
        }
    }
